import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../services/api';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';

function MinimalGroupPrintPage() {
  const navigate = useNavigate();
  const { group_id } = useParams();
  const { userProfile } = useUser();
  const businessProfile = userProfile?.group?.business_profile || {};

  // Fetch group details
  const {
    data: group,
    isLoading: groupLoading,
    isError: groupError,
  } = useQuery({
    queryKey: ['group-details', group_id],
    queryFn: async () => {
      const response = await API.get('/group/', { params: { group_id } });
      return response.data;
    },
    enabled: !!group_id,
  });

  // Fetch all group clients (unpaginated)
  const {
    data: groupClients = [],
    isLoading: clientsLoading,
    isError: clientsError,
  } = useQuery({
    queryKey: ['all-group-clients', group_id],
    queryFn: async () => {
      const resp = await API.get('/group/clients-all/', { params: { group_id } });
      return resp.data;
    },
    enabled: !!group_id,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!groupLoading && !clientsLoading && !groupError && !clientsError) {
      const timer = setTimeout(() => {
        printGroup();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [groupLoading, clientsLoading, group, groupError, clientsError]);

  const printGroup = () => {
    const htmlContent = generatePrintHTML(group, groupClients);
    openPrintWindow(htmlContent);
    navigate(-1);
  };

  const generatePrintHTML = (groupData, clientsData) => {
    const style = `
      <style>
        @media print {
          @page { margin: 12mm; }
          thead { display: table-header-group; }
          tr { page-break-inside: avoid; }
        }
        body {
          font-family: Arial, sans-serif;
          direction: rtl;
          margin: 0;
          padding: 20px;
        }
        .group-meta {
          margin-bottom: 20px;
        }
        .group-meta h1 {
          margin: 0;
          font-size: 24px;
        }
        .group-meta p {
          margin: 5px 0;
          font-size: 16px;
        }
        table {
          border-collapse: collapse;
          width: 100%;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 4px;
          text-align: center;
          font-size: 16px;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
      </style>
    `;

    const groupMetaHTML = `
      <div class="group-meta">
        <h1>${groupData.GroupName}</h1>
        <p><strong>סוג קבוצה:</strong> ${groupData.GroupType || ''}</p>
        <p><strong>תיאור:</strong> ${groupData.Description || 'אין תיאור'}</p>
        <p><strong>מספר לקוחות:</strong> ${clientsData.length}</p>
      </div>
    `;

    // Added new column for row numbers at the beginning
    const tableHeader = `
      <thead>
        <tr>
          <th>#</th>
          <th>שם לקוח</th>
          <th>תז/חפ</th>
        </tr>
      </thead>
    `;

    const rowsHTML = clientsData.map((client, index) => `
      <tr>
        <td>${index + 1}</td>
        <td>${client.ClientName || ''}</td>
        <td>${client.ClientBusinessID || ''}</td>
      </tr>
    `).join('');

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8" />
        ${style}
        <title>הדפסת פרטי קבוצה</title>
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        ${groupMetaHTML}
        <table>
          ${tableHeader}
          <tbody>
            ${rowsHTML}
          </tbody>
        </table>
      </body>
      </html>
    `;
  };

  if (groupLoading || clientsLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          טוען נתונים להדפסה...
        </Typography>
      </Box>
    );
  }

  if (groupError || clientsError) {
    return (
      <Box sx={{ p: 3, color: 'red' }}>
        <Typography variant="h6">
          שגיאה בטעינת נתוני הקבוצה להדפסה
        </Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(-1)}>
          חזרה לעמוד הקודם
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6">
        פתיחת חלון חדש להדפסה...
      </Typography>
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
        חזרה לעמוד הקודם
      </Button>
    </Box>
  );
}

export default MinimalGroupPrintPage;
